import { Pressable, Text, View } from 'react-native';
import { DecisionPill, ReviewedMark, StatusPill, wasReviewed } from '~/components/decision';
import { StageBar } from '~/components/stages';
import { Card } from '~/components/ui';
import type { JobListItem } from '~/lib/api';
import { AREA_LABELS } from '~/lib/areas';
import { isRunning } from '~/lib/jobStatus';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * A time for today, a weekday for anything older.
 *
 * The frame's own rule — its fixture runs "14:02, 13:31, 11:48, 10:20, 09:05, Tue, Tue".
 * A full date on every row is six characters an operator reads past to find the two that
 * separate this morning from last week.
 */
function when(iso: string): string {
  const at = new Date(iso);
  const today = new Date();
  const sameDay =
    at.getFullYear() === today.getFullYear() &&
    at.getMonth() === today.getMonth() &&
    at.getDate() === today.getDate();
  return sameDay
    ? at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : at.toLocaleDateString([], { weekday: 'short' });
}

/**
 * One journey, as a card.
 *
 * Shared because Recent journeys and the jobs list ARE the same card — the frame says so
 * in as many words ("Same card as Recent journeys; every card state in one list") — and
 * they had drifted into two: one with a stage bar and a running tone, one with neither and
 * a full date stamp.
 *
 * The vehicle, the claimed areas and the "Reviewed" mark arrived with 18.6 — until then
 * `GET /v1/jobs` returned an id, a status, a decision and a time, and the card showed the
 * id because there was nothing else to show. It leads with the vehicle now, which is what
 * an operator standing at one recognises; the id is the fallback, not the headline.
 */
export function JourneyCard({
  job,
  onPress,
  testID,
}: {
  job: JobListItem;
  onPress: () => void;
  testID?: string;
}) {
  const { c, t, space } = useTheme();
  const live = isRunning(job.status);
  const held = job.status === 'pending_review';

  /*
    The frame's own fallback when the VIN was skipped or never decoded. The short job id
    goes with it, because "Vehicle unknown" on its own does not tell two of them apart.
  */
  const v = job.vehicle;
  const vehicle = v
    ? [v.make, v.model, v.year].filter(Boolean).join(' ')
    : `Vehicle unknown · ${job.job_id.slice(0, 8)}`;
  const areas = (job.claimed_areas ?? []).map((a) => AREA_LABELS[a]).join(' · ');

  return (
    <Pressable testID={testID} accessibilityRole="button" onPress={onPress}>
      <Card style={{ gap: space.sm }} tone={live ? c.border2 : undefined}>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: space.sm }}>
          <Text style={[t.bodyLg, { fontWeight: '600', color: c.text, flex: 1 }]}>{vehicle}</Text>
          <Text style={[t.monoValue, { color: c.dim }]}>{when(job.created_at)}</Text>
        </View>
        {/*
          The panes claimed on this journey, lower case as the frame sets them — on a card
          an area is one fact among several, not a heading.
        */}
        {areas ? (
          <Text style={[t.body, { color: c.dim }]} numberOfLines={1}>
            {areas}
          </Text>
        ) : null}
        {/*
          Same pill, same word, same glyph as the desk console. A raw `pending_review` or
          `no_damage` never reaches a screen.
        */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.sm }}>
          {held ? (
            <StatusPill state="in_review" testID={`held-${job.job_id}`} />
          ) : live ? (
            <StatusPill state="analysing" />
          ) : job.decision ? (
            <DecisionPill decision={job.decision} />
          ) : job.status === 'failed' ? (
            <StatusPill state="failed" />
          ) : null}
          {wasReviewed(job.decision_source) ? (
            <ReviewedMark testID={`reviewed-${job.job_id}`} />
          ) : null}
        </View>
        {/*
          The stage bar, on any job that has not reached a verdict — running or held. Five
          segments, filled from the status, no percentage: the same truth the waiting
          timeline tells, at card size.
        */}
        <StageBar status={job.status} />
      </Card>
    </Pressable>
  );
}
